import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useCurrency } from '@/hooks/useCurrency';

export interface ArcaneSeason {
  id: string;
  season_number: number;
  name: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
}

export interface ArcaneSeasonProgress {
  id: string;
  user_id: string;
  season_id: string;
  season_wins: number;
  season_superpowers_used: number;
  challenges_completed: number;
  arcane_champion: boolean;
}

export interface SeasonTier {
  threshold: number;
  label: string;
  emoji: string;
  coins?: number;
  gems?: number;
  badge?: boolean;
}

export const SEASON_TIERS: SeasonTier[] = [
  { threshold: 5,  label: 'Apprentice',    emoji: '🌟', coins: 50 },
  { threshold: 15, label: 'Adept',          emoji: '💎', gems: 2 },
  { threshold: 30, label: 'Wizard',         emoji: '⚡', gems: 5 },
  { threshold: 50, label: 'Archmage',       emoji: '👑', coins: 100 },
  { threshold: 999, label: 'Arcane Champion', emoji: '🏆', badge: true },
];

export function useArcaneSeason() {
  const { user } = useAuth();
  const { awardCurrency } = useCurrency();
  const [season, setSeason] = useState<ArcaneSeason | null>(null);
  const [progress, setProgress] = useState<ArcaneSeasonProgress | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSeason = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('arcane_seasons')
        .select('*')
        .eq('is_active', true)
        .maybeSingle();
      setSeason(data ?? null);
      return data;
    } catch (err) {
      console.error('[ArcaneSeason] fetchSeason error:', err);
      return null;
    }
  }, []);

  const fetchProgress = useCallback(async (seasonId: string) => {
    if (!user) return null;
    try {
      const { data } = await supabase
        .from('arcane_season_progress')
        .select('*')
        .eq('user_id', user.id)
        .eq('season_id', seasonId)
        .maybeSingle();

      if (!data) {
        const { data: inserted } = await supabase
          .from('arcane_season_progress')
          .insert({ user_id: user.id, season_id: seasonId })
          .select()
          .single();
        setProgress(inserted ?? null);
        return inserted;
      }
      setProgress(data);
      return data;
    } catch (err) {
      console.error('[ArcaneSeason] fetchProgress error:', err);
      return null;
    }
  }, [user]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const s = await fetchSeason();
      if (s && user) await fetchProgress(s.id);
      setLoading(false);
    };
    init();
  }, [fetchSeason, fetchProgress, user]);

  const recordSeasonWin = useCallback(async () => {
    if (!user || !season) return;
    try {
      const { data: current } = await supabase
        .from('arcane_season_progress')
        .select('*')
        .eq('user_id', user.id)
        .eq('season_id', season.id)
        .maybeSingle();

      const prev = current?.season_wins ?? 0;
      const newWins = prev + 1;

      await supabase
        .from('arcane_season_progress')
        .upsert({
          user_id: user.id,
          season_id: season.id,
          season_wins: newWins,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id,season_id' });

      // Check if a new tier was crossed
      for (const tier of SEASON_TIERS) {
        if (tier.threshold === 999) continue; // champion is awarded at season end
        if (prev < tier.threshold && newWins >= tier.threshold) {
          if (tier.coins) await awardCurrency(tier.coins, 0, `arcane_season_tier_${tier.label.toLowerCase()}`, { season_id: season.id });
          if (tier.gems) await awardCurrency(0, tier.gems, `arcane_season_tier_${tier.label.toLowerCase()}`, { season_id: season.id });
        }
      }

      await fetchProgress(season.id);
    } catch (err) {
      console.error('[ArcaneSeason] recordSeasonWin error:', err);
    }
  }, [user, season, awardCurrency, fetchProgress]);

  const getDaysRemaining = () => {
    if (!season) return 0;
    const end = new Date(season.end_date);
    const now = new Date();
    return Math.max(0, Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
  };

  const getCurrentTierIndex = () => {
    const wins = progress?.season_wins ?? 0;
    let tier = -1;
    for (let i = 0; i < SEASON_TIERS.length - 1; i++) {
      if (wins >= SEASON_TIERS[i].threshold) tier = i;
    }
    return tier;
  };

  return {
    season,
    progress,
    loading,
    recordSeasonWin,
    getDaysRemaining,
    getCurrentTierIndex,
    SEASON_TIERS,
  };
}
